import { Chip, Grid } from '@mui/material';
import { HSpacer, RoutalDialog, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import CrossIcon from '../assets/svg/CrossIcon';
import { Supervisor } from '../domain/supervisor/Supervisor';
import { useUpdateStopsSupervisors } from '../hooks/stop/useUpdateStopsSupervisors';
import { useRetrieveAllSupervisors } from '../hooks/supervisor/useRetrieveAllSupervisors';
import Button from '../react-components/Button';
import Text from '../react-components/Text';

type StopsToSupervisorDialogProps = {
  open: boolean;
  stopIds: Array<string>;
  onCloseDialog: () => void;
};

export const StopsToSupervisorDialog = ({ open, stopIds, onCloseDialog }: StopsToSupervisorDialogProps) => {
  const [selectedSupervisors, setSelectedSupervisors] = useState<Array<Supervisor | `unassign_all`>>([]);
  const { data: supervisors } = useRetrieveAllSupervisors();
  const {
    run: updateSupervisors,
    isLoading: isLoadingUpdate,
    success: updateStopsSuccess,
  } = useUpdateStopsSupervisors();

  useEffect(() => {
    if (updateStopsSuccess) {
      onCloseDialog();
      setSelectedSupervisors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStopsSuccess]);

  const onAssignStops = (e: any) => {
    e.stopPropagation();
    if (selectedSupervisors.includes(`unassign_all`)) {
      updateSupervisors({
        stopIds: stopIds,
        supervisors: [],
      });
      return;
    }
    updateSupervisors({
      stopIds: stopIds,
      supervisors: (selectedSupervisors as Supervisor[]).map((supervisor) => supervisor.id),
    });
  };

  const isLoading = isLoadingUpdate;

  const isButtonDisabled = isLoading || selectedSupervisors.length === 0;

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={() => {
        setSelectedSupervisors([]);
        onCloseDialog();
      }}
      titleContent={<Text variant="h6" intlId="stops.assign_to_supervisor.title" />}
      titleRightContent={
        <Button
          variant="default"
          style={{ margin: 0, minWidth: `110px` }}
          disabled={isButtonDisabled}
          onClick={onAssignStops}
        >
          {isLoading ? (
            <Text intlId="loading" style={{ color: `white` }} />
          ) : (
            <Text intlId="save" style={{ color: `white`, cursor: `pointer` }} />
          )}
        </Button>
      }
      showFooter={false}
    >
      <Grid container direction="column" wrap="nowrap">
        <Grid item>
          <Text variant="label" intlId="stops.assign_to_supervisor.label" />
        </Grid>

        <VSpacer small />

        <Grid item>
          <RoutalSelect
            id="assign_to_supervisor_selector"
            disabled={isLoading}
            popoverProps={{
              options: [
                ...(supervisors ?? [])
                  .filter(
                    (supervisor) =>
                      !selectedSupervisors
                        .map((selectedSupervisor) =>
                          typeof selectedSupervisor === typeof `abc`
                            ? selectedSupervisor
                            : (selectedSupervisor as Supervisor).id
                        )
                        .includes(supervisor.id)
                  )
                  .map(
                    (supervisor, idx): RoutalSelectMultiSelectOption => ({
                      key: `plan-${supervisor.id ?? idx}`,
                      content: (
                        <Grid container direction="row" wrap="nowrap" justifyContent="flex-start" alignItems="center">
                          <div
                            style={{
                              width: `14px`,
                              height: `14px`,
                              backgroundColor: supervisor.color?.value,
                              borderRadius: `50%`,
                            }}
                          />
                          <HSpacer small />
                          <Text style={{ cursor: `pointer` }}>{supervisor.name}</Text>
                        </Grid>
                      ),
                      onClick: () =>
                        setSelectedSupervisors([
                          ...selectedSupervisors.filter((selectedSupervisor) => selectedSupervisor !== `unassign_all`),
                          supervisor,
                        ]),
                    })
                  ),
                {
                  key: `unassign_all`,
                  width: 500,
                  content: (
                    <Text style={{ cursor: `pointer` }} intlId="stops.assign_to_supervisor.option.unassign_all" />
                  ),
                  onClick: () => setSelectedSupervisors([`unassign_all`]),
                },
              ],
            }}
          >
            <Grid item container direction="row" wrap="nowrap" justifyContent="flex-start" alignItems="center">
              {selectedSupervisors.includes(`unassign_all`) ? (
                <Text intlId="stops.assign_to_supervisor.option.unassign_all" style={{ cursor: `pointer` }} />
              ) : (
                <Text intlId="stops.assign_to_supervisor.option.select_supervisor" style={{ cursor: `pointer` }} />
              )}
            </Grid>
          </RoutalSelect>
        </Grid>

        <VSpacer small />

        <Grid container direction="row">
          {!selectedSupervisors.includes(`unassign_all`)
            ? (selectedSupervisors as Supervisor[]).map((selectedSupervisor: Supervisor, index) => {
                const supervisor = (supervisors ?? []).find((supervisor) => supervisor.id === selectedSupervisor.id);
                if (!supervisor) {
                  return null;
                }

                return (
                  <Grid item>
                    <Chip
                      key={supervisor.id}
                      label={supervisor.name}
                      style={{
                        color: `white`,
                        backgroundColor: supervisor.color?.value,
                        marginLeft: index > 0 ? `8px` : `0px`,
                      }}
                      onDelete={() => {
                        setSelectedSupervisors(
                          (selectedSupervisors as Supervisor[]).filter(
                            (selectedSupervisor) => selectedSupervisor.id !== supervisor.id
                          )
                        );
                      }}
                      deleteIcon={
                        <CrossIcon
                          style={{
                            fontSize: `10px`,
                            marginLeft: `2px`,
                            marginRight: `8px`,
                            color: `white`,
                          }}
                        />
                      }
                    />
                  </Grid>
                );
              })
            : null}
        </Grid>

        <VSpacer medium />
      </Grid>
    </RoutalDialog>
  );
};
